import { Tab } from "@headlessui/react";
import Button from "src/components/ui/button";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { User } from "src/api/types";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import { TransactionDataType } from "src/lib/constants/table-type";
import { capitalizeFirstLetter, getStatusStyle } from "src/lib/utils";
import LimitationsBusinessPage from "./limitations-business";
import BusinessBillingLimitationsPage from "src/components/limitations/billings/business-limitations";
import BusinessGamblingLimitationsMgt from "src/components/limitations/gambling/business-limitation-mgt";
import usePermissions from "src/hooks/use-permissions";
import { permissions as permissionConstants } from "src/lib/constants";
import routes from "src/config/routes";

interface BusinessLimitationsManagementProps {
  isMoneyMovers?: boolean;
  permissionModule?: string | string[];
}

const BusinessLimitationsManagement: React.FC<BusinessLimitationsManagementProps> = ({
  isMoneyMovers,
  permissionModule: customPermissionModule,
}) => {
  const location = useLocation();
  const isMoneyMoversMode =
    isMoneyMovers ??
    (location.pathname === routes.limitationsMoneyMoversBusiness ||
      location.pathname.includes("money-movers"));

  const defaultPermission = isMoneyMoversMode
    ? permissionConstants.LIMITATIONS_MONEY_MOVERS_BUSINESS
    : permissionConstants.LIMITATION_BUSINESS;

  const permissionModule = customPermissionModule || defaultPermission;

  const { canView, canList } = usePermissions(permissionModule);
  const canViewLimitations = canView || canList;

  const tabLists = isMoneyMoversMode
    ? ["Billings", "Casino"]
    : ["Limitations"];

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [invoiceData, setInvoiceData] = useState<TransactionDataType | null>();
  const [selectedUserID, setSelectedUserID] = useState(-1);

  useEffect(() => {
    setSelectedTabIndex(0);
  }, [isMoneyMoversMode]);

  const makeTabList = () => {
    const tabListUI = tabLists.map((item, index) => (
      <Tab
        key={`tabs_${index}`}
        className={({ selected }) =>
          classNames(
            "h-full font-medium text-[14px] md:text-[16px] relative px-2 py-2 md:px-8 md:py-4 before:absolute before:left-0 before:bottom-0 before:h-0.5 before:bg-brand before:transition-all before:duration-300 before:ease-in-out",
            "ring-transparent ring-opacity-60 ring-offset-0 ring-offset-transparent focus:outline-none focus:ring-0",
            selected
              ? "text-primary bg-white rounded-t-[8px]"
              : "text-secondary bg-transparent"
          )
        }
      >
        {item}
      </Tab>
    ));
    return (
      <Tab.List className="flex flex-row items-center w-full h-[58px] md:h-[56px]">
        {tabListUI}
      </Tab.List>
    );
  };

  const renderActiveTabContent = () => {
    if (isMoneyMoversMode) {
      if (selectedTabIndex === 0) {
        return (
          <BusinessBillingLimitationsPage
            permissionModule={permissionModule}
          />
        );
      }
      return (
        <BusinessGamblingLimitationsMgt
          permissionModule={permissionModule}
        />
      );
    }
    return <LimitationsBusinessPage permissionModule={permissionModule} />;
  };

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full flex flex-col">
        {!canViewLimitations ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] m-4 p-8 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view limitations.
            </p>
          </div>
        ) : selectedUser == null ? (
          <div
            className={`${
              invoiceData && "hidden"
            } w-full h-full flex-col p-2 sm:p-4`}
          >
            <Tab.Group
              selectedIndex={selectedTabIndex}
              onChange={(tabIdx: number) => setSelectedTabIndex(tabIdx)}
            >
              {makeTabList()}
            </Tab.Group>
            <div className="mt-4 flex items-center justify-between w-full mb-4">
              {renderActiveTabContent()}
            </div>
          </div>
        ) : (
          <div
            className={`${
              invoiceData && "hidden"
            } flex flex-col w-full p-2 sm:p-4`}
          >
            <div className="flex flex-row items-center justify-between sm:justify-start gap-4">
              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="icon"
                  className="w-[24px] bg-transparent h-[24px]"
                  onClick={() => {
                    setSelectedUserID(-1);
                    setSelectedUser(null);
                  }}
                >
                  <BackCircleIcon className="w-[24px] h-[24px] text-primary" />
                </Button>
                <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[38px] sm:leading-[48px]">
                  {!selectedUser.user_data.first_name &&
                  !selectedUser.user_data.last_name
                    ? "-"
                    : (selectedUser.user_data.first_name ?? " ") +
                      " " +
                      (selectedUser.user_data.last_name ?? " ")}
                </p>
              </div>
              <p
                className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
                  selectedUser.status.name
                )}`}
              >
                {capitalizeFirstLetter(selectedUser.status.name)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessLimitationsManagement;
